import React, { useState, useRef, useEffect } from 'react';
import { Route, Routes } from 'react-router-dom';
import GetStartedPage from './pages/GetStartedPage';
import BlogWriterPage from './pages/BlogWriterPage';
import { BlogContext, BlogContextType } from './BlogContext';
import { BlogContent } from './types';
import endpoints from './aiBlogWriterEndPoints';
import axiosInstance from 'api/axios';
import remarkHtml from 'remark-html';
import remarkMdx from 'remark-mdx';
import remarkParse from 'remark-parse';
import { unified } from 'unified';
import HistoryPage from './pages/HistoryPage';
import Layout from 'components/Layout';
import toast from 'react-hot-toast';
import { extractImagesFromMarkdown } from './components/Modals/blogSaveAs';
export const DEFAULT_BLOG_CONTENT: BlogContent = {
  title: '',
  content: '',
  images: [],
  wordCount: 1,
  markDownContent: '',
  prevState: '',
  settings: {
    minLength: '0',
    maxLength: '0',
    language: '',
    toneOfVoice: '',
    industry: '',
    imageCount: 0,
    permission: 2,
  },
};
const AiBlogWriter = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSettingBlog, setIsSettingBlog] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [blogContent, setBlogContent] = useState<BlogContent | null>(
    DEFAULT_BLOG_CONTENT
  );
  const [htmlDump, setHtmlDump] = useState('');
  const [requestCancel, setRequestCancel] = useState(false);

  const cleanUp = () => {
    if (abortControllerRef.current) {
      setRequestCancel(true);
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsGenerating(false);
      // Only show toast if this wasn't triggered by component unmount
      if (document.body) {
        toast.success('Blog generation cancelled successfully');
      }
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
        setIsGenerating(false);
      }
    };
  }, []);

  const handleUpdateBlogContent = async (formData: BlogContent) => {
    setIsSettingBlog(true);
    try {
      setBlogContent(formData);
      // Add error handling here if needed
    } catch (error) {
      console.error('Error updating blog content:', error);
      toast.error('Failed to update blog content');
    } finally {
      setIsSettingBlog(false);
    }
  };

  const handleUpdateHtmlDump = async (htmlString: string) => {
    setHtmlDump(htmlString);
  };

  const handleGenerateBlog = async (formData: {
    topic: string | null;
    language: string | null;
    img_number: number | null;
    tone: string | null;
    industry: string | null;
    min_len: number | null;
    max_len: number | null;
  }) => {
    // Reset cancel state at the start of new generation
    setRequestCancel(false);

    // If there's an ongoing request, cancel it
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();
    setIsGenerating(true);

    try {
      const response = await axiosInstance.public.post(
        endpoints.GenerateBlog,
        formData,
        {
          signal: abortControllerRef.current.signal,
        }
      );

      // Only process the response if the request wasn't aborted and we haven't cancelled
      if (!requestCancel && !abortControllerRef.current?.signal.aborted) {
        const htmlString = await unified()
          .use(remarkParse)
          .use(remarkMdx)
          .use(remarkHtml)
          .process(response.data);

        handleUpdateHtmlDump(htmlString.toString());
        const imageUrls = extractImagesFromMarkdown(response.data);
        const blogData = {
          title: String(formData?.topic),
          content: htmlString.toString(),
          images: imageUrls ? imageUrls.split(',').filter(Boolean) : [],
          wordCount: 0,
          markDownContent: response.data,
          prevState: htmlString.toString(),
          settings: {
            minLength: String(formData.min_len),
            maxLength: String(formData.max_len),
            language: String(formData.language),
            toneOfVoice: String(formData.tone),
            industry: String(formData.industry),
            imageCount: Number(formData.img_number),
            permission: 2,
          },
        };
        handleUpdateBlogContent(blogData);
        toast.success('Blog generated successfully');
      }
    } catch (error: any) {
      // Check for both abort error and manual cancellation
      if (error?.name === 'CanceledError' || requestCancel) {
        console.log(
          'Request was cancelled:',
          requestCancel ? 'manual cancel' : 'abort signal'
        );
        return;
      }

      console.error('Error generating blog:', error);
      toast.error('Failed to generate blog content');
      throw error;
    } finally {
      // Reset states if this wasn't a cancellation
      if (!requestCancel && !abortControllerRef.current?.signal.aborted) {
        setIsGenerating(false);
        abortControllerRef.current = null;
      }
      // Reset requestCancel state
      setRequestCancel(false);
    }
  };

  const value: BlogContextType = {
    blogContent,
    isGenerating,
    handleGenerateBlog,
    handleUpdateBlogContent,
    htmlDump,
    handleUpdateHtmlDump,
    cleanUp,
    isSettingBlog,
  };

  return (
    <BlogContext.Provider value={value}>
      <Layout>
        <Routes>
          <Route path="/get-started" element={<GetStartedPage />} />
          <Route path="/blog" element={<BlogWriterPage />} />
          <Route path="/blog/:id" element={<BlogWriterPage />} />
          <Route path="/history" element={<HistoryPage />} />
        </Routes>
      </Layout>
    </BlogContext.Provider>
  );
};

export default AiBlogWriter;
